import { useCallback, useMemo, useState } from "react";
import "@visioni/a3s-ui/theme";
import { StyleSheet } from "react-native-unistyles";
import { View, Autocomplete, Banner, Button, Icon, Input, SingleDatePicker, Text } from "@visioni/a3s-ui/components";
import { COMPILATION_MAX_RANGE_DAYS, COMPILATION_QUEUE_LIMIT } from "../../../client-messages/compilation";
import { formatFootageDuration, type ClipCountEstimate } from "./clip-count";
import { earliestStartDate, latestEndDate, timezoneOffsetLabel } from "./date-range";
import type { FormField } from "./useRequestCompilationForm";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RequestCompilationFormProps<TCamera extends object> {
  readonly cameras: readonly TCamera[];
  readonly getCameraLabel: (camera: TCamera) => string;
  readonly getCameraId: (camera: TCamera) => string;
  readonly camera: FormField<string | null>;
  /**
   * The timezone the entered dates and times are read in — the selected
   * camera's. Shown to the reader once a camera is chosen, because the times
   * they type are otherwise silently assumed to be their own zone's.
   */
  readonly timezone: string;
  readonly startDate: FormField<Date | null>;
  readonly startTime: FormField<string>;
  readonly endDate: FormField<Date | null>;
  readonly endTime: FormField<string>;
  readonly canSubmit: boolean;
  readonly onSubmit: () => void;
  readonly loading?: boolean | undefined;
  /** Maximum selectable date. */
  readonly maxDate?: Date | undefined;
  /**
   * How many clips the current selection covers. Omit on a form with no count
   * behind it (the Storybook stories, and any caller that has none) — the row
   * is then simply absent, as it is before a selection is complete.
   */
  readonly clipCount?: ClipCountEstimate | undefined;
  /** Why the server refused the last request, in its own words, e.g. a full camera queue. */
  readonly requestRefusal?: string | undefined;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function RequestCompilationFormInner<TCamera extends object>({
  cameras,
  getCameraLabel,
  getCameraId,
  camera,
  timezone,
  startDate,
  startTime,
  endDate,
  endTime,
  canSubmit,
  onSubmit,
  loading,
  maxDate,
  clipCount,
  requestRefusal,
}: RequestCompilationFormProps<TCamera>) {
  const selectedCamera = useMemo(
    () => cameras.find((c) => getCameraId(c) === camera.value) ?? null,
    [cameras, camera.value, getCameraId],
  );

  // Matches the ordering of the filter row's camera picker — the two list the
  // same cameras, so they shouldn't disagree on their order.
  const sortedCameras = useMemo(
    () =>
      [...cameras].sort((a, b) =>
        getCameraLabel(a).localeCompare(getCameraLabel(b), undefined, { numeric: true, sensitivity: "base" }),
      ),
    [cameras, getCameraLabel],
  );

  // Which date field's calendar is open, if any. The calendar flips above its
  // field when the dialog leaves no room below, so the open field's wrapper has
  // to out-rank the row above it — otherwise the flipped calendar paints behind
  // that row and its dates can't be clicked.
  const [openPicker, setOpenPicker] = useState<"start" | "end" | null>(null);
  const handleStartOpenChange = useCallback((open: boolean) => {
    setOpenPicker((current) =>
      open ? "start"
      : current === "start" ? null
      : current,
    );
  }, []);
  const handleEndOpenChange = useCallback((open: boolean) => {
    setOpenPicker((current) =>
      open ? "end"
      : current === "end" ? null
      : current,
    );
  }, []);

  const handleCameraChange = useCallback(
    (value: TCamera | null) => {
      camera.onChange(value ? getCameraId(value) : null);
    },
    [getCameraId, camera],
  );

  // Keeps the pickers from letting a span wider than the backend's own limit
  // (`RequestCompilationBody`'s refine check in `client-messages/compilation.ts`)
  // be selected in the first place, on top of the overall `maxDate` ceiling.
  // Both bounds account for the entered times — see `date-range.ts` for why a
  // whole-day count alone offered spans the form then rejected.
  const startDateMinDate =
    endDate.value ?
      earliestStartDate({ endDate: endDate.value, startTime: startTime.value, endTime: endTime.value, timezone })
    : undefined;
  const endDateMaxDate =
    startDate.value ?
      latestEndDate({
        startDate: startDate.value,
        startTime: startTime.value,
        endTime: endTime.value,
        timezone,
        maxDate,
      })
    : maxDate;

  const startRowZIndex = openPicker === "start" ? 4 : 2;
  const endRowZIndex = openPicker === "end" ? 4 : 1;

  return (
    <View style={styles.container}>
      <Banner
        variant="info"
        icon={<Icon name="info" size={16} />}
        title="Compilation limits"
        message={`Maximum range is ${String(COMPILATION_MAX_RANGE_DAYS)} days per request. Longer selections take more time to process.\nClips are taken directly from the camera, footage may have been overwritten for older recordings.\nUp to ${String(COMPILATION_QUEUE_LIMIT)} compilations can be queued or running per camera; a new one starts once those ahead of it finish.`}
      />

      <View style={[styles.field, { zIndex: 3 }]}>
        <Autocomplete<TCamera>
          options={sortedCameras}
          value={selectedCamera}
          onChange={handleCameraChange}
          getOptionLabel={getCameraLabel}
          getOptionId={getCameraId}
          placeholder="Select camera"
          label="Camera"
        />
      </View>

      {camera.value !== null && (
        <Text textStyle="sm/regular" color="textMuted">
          {`Times are in the camera's timezone — ${timezone} (${timezoneOffsetLabel(timezone, new Date())})`}
        </Text>
      )}

      <View style={[styles.row, { zIndex: startRowZIndex }]}>
        <View style={[styles.dateField, { zIndex: startRowZIndex }]}>
          <SingleDatePicker
            value={startDate.value}
            onChange={startDate.onChange}
            label="Start date"
            placeholder="Select date"
            minDate={startDateMinDate}
            maxDate={endDate.value ?? maxDate}
            onOpenChange={handleStartOpenChange}
            clearable
          />
        </View>
        <View style={styles.timeField}>
          <Input
            value={startTime.value}
            onChangeText={startTime.onChange}
            label="Start time"
            placeholder="HH:MM"
            error={startTime.error}
          />
        </View>
      </View>

      <View style={[styles.row, { zIndex: endRowZIndex }]}>
        <View style={[styles.dateField, { zIndex: endRowZIndex }]}>
          <SingleDatePicker
            value={endDate.value}
            onChange={endDate.onChange}
            label="End date"
            placeholder="Select date"
            minDate={startDate.value ?? undefined}
            maxDate={endDateMaxDate}
            onOpenChange={handleEndOpenChange}
            clearable
          />
        </View>
        <View style={styles.timeField}>
          <Input
            value={endTime.value}
            onChangeText={endTime.onChange}
            label="End time"
            placeholder="HH:MM"
            error={endTime.error}
          />
        </View>
      </View>

      {clipCount && <ClipCountRow estimate={clipCount} />}

      {requestRefusal && (
        // `Banner` sets no role or live region of its own.
        <View accessibilityRole="alert" accessibilityLiveRegion="polite">
          <Banner
            variant="warning"
            icon={<Icon name="warning" size={16} />}
            title="Compilation not requested"
            message={requestRefusal}
          />
        </View>
      )}

      <View style={styles.actions}>
        <Button
          label="Request"
          variant="primary"
          size="md"
          onPress={onSubmit}
          disabled={!canSubmit}
          loading={loading ?? false}
          loadingText="Requesting…"
        />
      </View>
    </View>
  );
}

/**
 * What the selected range holds, shown before the job can be requested.
 *
 * Worded as an estimate throughout: the compilation is rendered from the
 * camera's own recordings, so the clips counted here indicate how much the
 * range covers rather than promising what the finished video will contain.
 */
function ClipCountRow({ estimate }: { readonly estimate: ClipCountEstimate }) {
  switch (estimate.state) {
    case "unselected":
      return null;
    case "counting":
      return (
        <Text textStyle="sm/regular" color="textMuted">
          Counting clips in this range…
        </Text>
      );
    case "unavailable":
      return (
        <Banner
          variant="warning"
          icon={<Icon name="warning" size={16} />}
          title="Clip count unavailable"
          message="We couldn't check how many clips this range covers. You can still request the compilation."
        />
      );
    case "counted":
      if (estimate.count === 0) {
        return (
          <Banner
            variant="warning"
            icon={<Icon name="warning" size={16} />}
            title="No clips in this range"
            message="This camera has no clips between the selected times. Widen the range, or pick another camera."
          />
        );
      }
      return (
        <Text textStyle="sm/regular" color="textMuted">
          {`About ${String(estimate.count)} ${estimate.count === 1 ? "clip" : "clips"} · ${formatFootageDuration(
            estimate.totalDurationMs,
          )} of footage`}
        </Text>
      );
  }
}

RequestCompilationFormInner.displayName = "RequestCompilationForm";

const RequestCompilationForm = RequestCompilationFormInner as <TCamera extends object>(
  props: RequestCompilationFormProps<TCamera>,
) => ReturnType<typeof RequestCompilationFormInner>;

export { RequestCompilationForm };

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create((theme) => ({
  container: {
    gap: theme.spacing.xl,
  },
  field: {
    // full width
  },
  row: {
    flexDirection: "row",
    gap: theme.spacing.lg,
    alignItems: "flex-start",
  },
  dateField: {
    flex: 2,
  },
  timeField: {
    flex: 1,
    minWidth: 100,
  },
  actions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    paddingTop: theme.spacing.md,
  },
}));
